import React, { useEffect, useRef, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { Dispatch } from "redux";
import { IStore } from "redux/store";
import DatePicker from "./_elements/datepicker/DatePicker";
import { rootRoutes } from "routes";
import { historyActions } from "redux/_core/history";
import classNames from "classnames";
import { RouteProps, withRouter } from "react-router-dom";
import { vesselSelectors } from "redux/app/vessel";
import vdmDashboardActions from "redux/app/vdmdashboard/actions";
import vdmDashboardSelectors from "redux/app/vdmdashboard/selectors";
import { formatDistanceToNow } from "date-fns";
// import Skeleton from "react-loading-skeleton";
// import useElementSize from "hooks/useElementSize";
import {
	// ICard1Data,
	ICard2Data,
	// ICard3Data,
	ICNRForDateRangeParams,
	IGetCNRDateRangeParams,
	IVDMDashboardLoadParams,
	VDM_DASHBOARD_TABS,
} from "redux/app/vdmdashboard/@types";
import SideDrawer from "components/_common/SideDrawer";
import AlertsSidebarHeader from "./_elements/AlertsSidebarHeader";
import AlertsSidebarContent from "./_elements/AlertsSidebarContent";
import Card1 from "./dashboard/Card1";
// import { vdmVesselStatusData } from "redux/app/vdmdashboard/data";
import Card2 from "./dashboard/Card2";
import Card3 from "./dashboard/Card3";
import Card4 from "./dashboard/Card4";
import ModalPortal from "../_common/ModalPortal";
import ConsumptionGuidelinesModal from "./dashboard/ConsumptionGuidelinesModal";
import { vdmDashboardUtils } from "redux/app/vdmdashboard/utils";
import { addDays } from "date-fns";
import SkeletonContainer from "./_elements/SkeletonLoader";

const { getMonthlyRange, toUTCMidnight } = vdmDashboardUtils;

const getCNRDateRange = (params: IGetCNRDateRangeParams) => {
	const { date, loadCNRDateRange, vesselId } = params;
	const { start, end } = getMonthlyRange(date);

	loadCNRDateRange({
		vesselId,
		start,
		end,
	});
};

interface ITab {
	id: VDM_DASHBOARD_TABS;
	label: string;
	path: string;
	icon?: string;
	url: (vesselId: string) => string;
	isDisabled?: boolean;
}

const {
	bunkerRobs,
	bunkerConsumption,
	lubeOils,
	chemicalRobs,
	security,
	weather,
	portCalls,
	inspection,
	stoppages,
	lngConsumption,
	pic,
} =
	rootRoutes.children.app.children.vessel.children.onboard.children.vdmdashboard
		.children;

const vdmTabNavigation: ITab[] = [
	{
		id: VDM_DASHBOARD_TABS.PORT_CALLS,
		label: "Port Calls",
		path: "/port-calls",
		url: portCalls.url,
	},
	{
		id: VDM_DASHBOARD_TABS.BUNKER_ROBS,
		label: "Bunker ROBs",
		path: "/bunker-robs",
		url: bunkerRobs.url,
	},
	{
		id: VDM_DASHBOARD_TABS.LUBE_OILS,
		label: "Lube Oils",
		path: "/lube-oils",
		url: lubeOils.url,
		isDisabled: true /* INFO: Not part of phase-1 */,
	},
	{
		id: VDM_DASHBOARD_TABS.LNG_CONSUMPTION,
		label: "LNG Consumption",
		path: "/lng-consumption",
		url: lngConsumption.url,
		isDisabled: true /* INFO: Not part of phase-1 */,
	},
	{
		id: VDM_DASHBOARD_TABS.BUNKER_CONSUMPTION,
		label: "Bunker Consumption",
		path: "/consumption",
		url: bunkerConsumption.url,
	},
	{
		id: VDM_DASHBOARD_TABS.CHEMICAL_ROBS,
		label: "Chemical Robs",
		path: "/chemical-robs",
		url: chemicalRobs.url,
		isDisabled: true /* INFO: Not part of phase-1 */,
	},
	{
		id: VDM_DASHBOARD_TABS.WEATHER,
		label: "Weather",
		path: "/weather",
		url: weather.url,
	},
	{
		id: VDM_DASHBOARD_TABS.INSPECTION,
		label: "Inspection",
		path: "/inspection",
		url: inspection.url,
		isDisabled: true /* INFO: Not part of phase-1 */,
	},
	{
		id: VDM_DASHBOARD_TABS.SECURITY,
		label: "Security",
		path: "/security",
		url: security.url,
		isDisabled: true /* INFO: Not part of phase-1 */,
	},
	{
		id: VDM_DASHBOARD_TABS.STOPPAGES,
		label: "Stoppages",
		path: "/stoppages",
		url: stoppages.url,
	},
	{
		id: VDM_DASHBOARD_TABS.PIC,
		label: "PIC",
		path: "/pic",
		url: pic.url,
		isDisabled: false /* INFO: Not part of phase-1 */,
	},
];

type OwnProps = {
	match: {
		params: {
			vesselId: string;
		};
	};
};

type VDMDashboardProps = React.PropsWithChildren<
	RouteProps & OwnProps & PropsFromRedux
>;

function VDMDashboard(props: VDMDashboardProps) {
	const vesselId = props.match.params.vesselId;
	const {
		children,
		activeTab,
		isAlertsSidebarExpanded,
		jobToLoadSmartAlerts,
		smartAlerts,
		cnrDataForDateRange,
		isCNRForDateRangeLoading,
		selectedVDMDashboardDate,
		selectedDateCNRId,
		loadVDMDashboardContent,
		dashboardContent,
		isDashboardContentLoading,
	} = props;
	const skeletonContainerRef = useRef<HTMLDivElement | null>(null);
	const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
	const calendarMonthPickerRef = useRef<{
		hasShownMonthChanged: boolean;
	}>({ hasShownMonthChanged: false });

	const [isConsumptionGuidelinesOpen, setIsConsumptionGuidelinesOpen] =
		useState(false);

	// const skeletonDim = useElementSize(skeletonContainerRef);
	// const isSmallScreen = window.innerWidth < 768;
	// const { width: mySkeletonWidth } = skeletonDim;

	// const skeletonWidth = mySkeletonWidth || window.innerWidth;

	useEffect(() => {
		props.setSelectedVDMDashboardDate(toUTCMidnight(new Date()).toISOString());
		props.loadVDMDashboardContent({ vesselId });
		// eslint-disable-next-line react-hooks/exhaustive-deps

		return () => {
			calendarMonthPickerRef.current.hasShownMonthChanged = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (vesselId) {
			// props.vdmVesselStatusLoad(vesselId);
			// props.loadVDMSmartAlerts(vesselId);
			props.setActiveTab(VDM_DASHBOARD_TABS.PORT_CALLS);
			props.onTabChange(portCalls.url(vesselId));
			// props.onTabChange(vdmTabNavigation[0]?.url(vesselId));
			window?.scrollTo({ top: 0, behavior: "smooth" });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [vesselId]);

	// Get active tab
	useEffect(() => {
		if (props?.location?.pathname) {
			const { pathname } = props.location;
			const activeTab = vdmTabNavigation.find((tab) => {
				return pathname.includes(tab.path);
			});
			if (activeTab) {
				props.setActiveTab(activeTab.id);
			}
		} else {
			props.setActiveTab(VDM_DASHBOARD_TABS.PORT_CALLS);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props?.location?.pathname]);

	// TODO: cnrRanges The limit and skip needs to be removed after proper signalReports range api
	useEffect(() => {
		getCNRDateRange({
			date: selectedVDMDashboardDate
				? new Date(selectedVDMDashboardDate)
				: undefined,
			vesselId,
			loadCNRDateRange: props.loadCNRForDateRange,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [vesselId]);

	const lastLoadedKey = useRef<string>("");
	const pendingVesselChangeRef = useRef<boolean>(false); // month navigation doesn't set this to true, but below effect does.
	const prevVesselIdRef = useRef<string>("");

	// Detect vessel change.
	useEffect(() => {
		if (prevVesselIdRef.current && prevVesselIdRef.current !== vesselId) {
			pendingVesselChangeRef.current = true;
			lastLoadedKey.current = "";
		}
		prevVesselIdRef.current = vesselId;
	}, [vesselId]);

	const rangeDataChange = cnrDataForDateRange?.map((rep) => rep._id).join(",");

	// Sync cnrId + handle vessel-change content load
	// This fires loadVDMDashboardContent after a vessel change.
	useEffect(() => {
		if (!cnrDataForDateRange || !selectedVDMDashboardDate) return;

		const matchingCnr = cnrDataForDateRange.find(
			(cnr) => cnr.selectedDate === selectedVDMDashboardDate,
		);

		// Setting the new cnrId
		if (matchingCnr && matchingCnr._id !== selectedDateCNRId) {
			props.setCNRIdForSelectedDate(matchingCnr._id);
		}

		// Only fire content load if a vessel change was pending
		// Month navigation(When months are being changed): pendingVesselChangeRef is false and hence content load api call is skipped
		if (!pendingVesselChangeRef.current) return;

		pendingVesselChangeRef.current = false;

		const currentKey = `${vesselId}-${selectedVDMDashboardDate}`;
		lastLoadedKey.current = currentKey;

		if (matchingCnr) {
			props.loadVDMDashboardContent({ vesselId, cnrId: matchingCnr?._id });
		} else {
			props.loadVDMDashboardContent({ vesselId });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rangeDataChange]);

	const handleOnChange = (date: string) => {
		// Handle date change
		props.setSelectedVDMDashboardDate(date);
	};

	const isLoading = isCNRForDateRangeLoading || isDashboardContentLoading;

	const isThereNoContent = !isLoading && !dashboardContent;
	const lastUpdatedDate = dashboardContent?.lastUpdatedAt
		? new Date(dashboardContent.lastUpdatedAt)
		: null;

	return (
		<>
			<SideDrawer
				isOpen={smartAlerts?.length ? isAlertsSidebarExpanded : false}
				onClose={() => {
					props.toggleAlertsSidebar(false);
				}}
				header={<AlertsSidebarHeader smartAlerts={smartAlerts} />}
			>
				<AlertsSidebarContent smartAlerts={smartAlerts} />
			</SideDrawer>
			<div className="my-2 bg-white md:rounded-md md:m-2">
				<div className="pt-4">
					<div className="flex flex-col lg:flex-row items-center justify-between">
						<div className="flex flex-col sm:flex-row items-center md:pb-2 lg:pb-0">
							<div className="flex items-center pl-0 px-2 lg:px-4 py-1 text-[12px] xl:text-sm rounded lg:rounded-full border border-[#EAECF0]">
								<span className="mr-1">
									{" "}
									Last updated{" "}
									{lastUpdatedDate && !isLoading
										? formatDistanceToNow(lastUpdatedDate, { addSuffix: true })
										: "N/A"}
								</span>
								<button
									onClick={() => {
										props.loadVDMDashboardContent({
											vesselId,
											cnrId: selectedDateCNRId || undefined,
										});
									}}
									className="text-blue-500 flex items-center"
								>
									<span className="mx-0.5">Refresh</span>
									<span className="bp3-icon bp3-icon-repeat ml-0.5"></span>
								</button>
							</div>
						</div>
						<div className="flex flex-wrap justify-center items-center mt-4 sm:mt-0 gap-2 xl:gap-4">
							<div>
								<DatePicker
									id="date-picker"
									handleOpen={() => {
										if (isLoading) return;
										calendarMonthPickerRef.current = {
											hasShownMonthChanged: false,
										};
										setIsDatePickerOpen(true);
									}}
									// For datepicker selection close
									closeOnSelection={() => {
										setIsDatePickerOpen(false);
									}}
									onClose={() => {
										setIsDatePickerOpen(false);
										if (calendarMonthPickerRef.current.hasShownMonthChanged) {
											getCNRDateRange({
												date: selectedVDMDashboardDate
													? new Date(selectedVDMDashboardDate)
													: undefined,
												vesselId,
												loadCNRDateRange: props.loadCNRForDateRange,
											});
										}
									}}
									isLoading={isLoading}
									cnrDataForDateRange={cnrDataForDateRange}
									isOpen={isDatePickerOpen}
									handleOnChange={handleOnChange}
									loadVDMDashboardContent={(cnrId: string) =>
										loadVDMDashboardContent({ cnrId, vesselId })
									}
									onShownDateChange={(date: Date) => {
										calendarMonthPickerRef.current.hasShownMonthChanged = true;
										date = addDays(date, 1);

										getCNRDateRange({
											date,
											vesselId,
											loadCNRDateRange: props.loadCNRForDateRange,
										});
									}}
									selectedCNRId={selectedDateCNRId}
									setSelectedCNRId={props.setCNRIdForSelectedDate}
									isCNRForDateRangeLoading={isCNRForDateRangeLoading}
									selectedDate={selectedVDMDashboardDate}
									className="xl:h-[38px] py-1 sm:py-1.5 text-xs lg:text-[12px] xl:text-sm rounded-md"
								/>
							</div>
							<button
								onClick={() => setIsConsumptionGuidelinesOpen(true)}
								className="flex items-center  px-4 lg:py-1.5  text-[12px] lg:text-[12px] xl:text-sm rounded border border-[#EAECF0] h-[32px] md:h-[34px] xl:h-[38px]  shadow-sm hover:shadow"
							>
								<span className="bp3-icon bp3-icon-manual mr-2" />
								<p>Consumption guidelines</p>
							</button>
							<button className="p-1 px-[9px] rounded shadow-sm bg-white border inline-block h-[32px] md:h-[34px] xl:h-[38px]">
								<span className="bp3-icon bp3-icon-import" />
							</button>
							<button
								onClick={() => {
									/* INFO: Not part of phase-1 */
									// props.toggleAlertsSidebar(true);
								}}
								className="p-1 px-[9px] rounded shadow-sm bg-white border inline-block h-[32px] md:h-[34px] xl:h-[38px] relative cursor-not-allowed"
							>
								<span className="bp3-icon bp3-icon-warning-sign text-red-500" />
								{jobToLoadSmartAlerts.isLoading ? (
									<span className="absolute animate-ping bg-red-400 rounded-full w-1.5 h-1.5 -top-[5px] -right-[2px] inline-block  text-[12px] font-medium" />
								) : (
									<span className="absolute -top-2 -right-1 inline-block  p-0.5 px-1.5 bg-red-500 text-red-100 text-[9px] rounded font-medium">
										{smartAlerts?.length}
									</span>
								)}
							</button>
						</div>
					</div>

					<div
						ref={skeletonContainerRef}
						className="flex justify-stretch items-stretch flex-wrap md:flex-nowrap gap-4 pt-4"
					>
						{isLoading ? (
							<SkeletonContainer cardCount={4} />
						) : !cnrDataForDateRange?.length ? (
							<div className="flex justify-center min-h-28">
								No data available for the selected month. Please try changing
								the month.
							</div>
						) : isThereNoContent ? (
							<div className="flex justify-center min-h-28">
								No data available for the selected date.
							</div>
						) : (
							<div className="md:overflow-x-auto flex flex-wrap flex-col md:flex-row md:flex-nowrap min-h-[200px] w-full gap-x-4 h-full 2xl:grid 2xl:grid-cols-4">
								<Card1
									data={dashboardContent?.currentlyAt}
									// data={vdmVesselStatusData[0] as unknown as ICard1Data}
								/>
								<Card2
									// data={vdmVesselStatusData[1] as unknown as ICard2Data}
									data={dashboardContent?.cargo}
								/>
								<Card3
									data={dashboardContent?.operationalStatus}
									// data={vdmVesselStatusData[2] as unknown as ICard3Data}
								/>
								<Card4
									data={dashboardContent?.navigation}
									// data={vdmVesselStatusData[3] as unknown as ICard1Data}
								/>
							</div>
						)}
					</div>

					{isLoading ? null : (!isLoading && !dashboardContent) ||
					  !cnrDataForDateRange?.length ? null : (
						<div className="mt-4">
							<div className="w-full border-b border-b-[#EAECF0] flex gap-2 overflow-x-auto text-sm md:text-base">
								{vdmTabNavigation.map((tab) => {
									const { id, label, url, isDisabled } = tab;
									return (
										<button
											key={id}
											className={classNames(
												`px-2 xl:px-4 py-1.5 lg:py-2 rounded-sm text-[12px] lg:text-sm font-medium whitespace-nowrap`,
												{
													"text-blue-500 border-b-2 border-blue-500":
														activeTab === id,
													"bg-white text-gray-700 border-b-2 border-transparent":
														activeTab !== id && !isDisabled,
													"bg-white text-gray-500 border-b-2 border-transparent cursor-not-allowed":
														isDisabled,
												},
											)}
											onClick={() => {
												if (isDisabled) return;
												props.setActiveTab(id);
												return props.onTabChange(url(vesselId));
											}}
										>
											{label}
										</button>
									);
								})}
							</div>
							<div className="rounded-md rounded-t-none">{children}</div>
						</div>
					)}
				</div>
			</div>
			{isConsumptionGuidelinesOpen ? (
				<ModalPortal
					isActive={isConsumptionGuidelinesOpen}
					onClose={() => setIsConsumptionGuidelinesOpen(false)}
					width={768}
				>
					<ConsumptionGuidelinesModal
						onClose={() => setIsConsumptionGuidelinesOpen(false)}
					/>
				</ModalPortal>
			) : null}
		</>
	);
}

function mapStateToProps(store: IStore, ownProps: OwnProps) {
	const vesselStore = vesselSelectors._getStore(store);
	const vdmDashboardStore = vdmDashboardSelectors._getStore(store);
	const vessel = vesselSelectors.getVesselById(
		ownProps.match.params.vesselId,
		vesselSelectors.getVessels(vesselStore),
	);
	const activeTab = vdmDashboardSelectors.getVDMActiveTab(vdmDashboardStore);
	const vdmVesselStatus =
		vdmDashboardSelectors.getVDMVesselStatus(vdmDashboardStore);
	const smartAlerts =
		vdmDashboardSelectors.getVDMSmartAlerts(vdmDashboardStore);
	const cnrDataForDateRange =
		vdmDashboardSelectors.getVDMCNRRecordsForDateRange(vdmDashboardStore);
	const selectedVDMDashboardDate =
		vdmDashboardSelectors.getVDMDashboardSelectedDate(vdmDashboardStore);
	const selectedDateCNRId =
		vdmDashboardSelectors.getSelectedDateCNRId(vdmDashboardStore);
	const dashboardContent =
		vdmDashboardSelectors.getVDMDashboardContent(vdmDashboardStore);

	const isAlertsSidebarExpanded =
		vdmDashboardSelectors.getIsVDMAlertsExpanded(vdmDashboardStore);
	const jobToLoadVesselVDMStatus =
		vdmDashboardSelectors.getJobToLoadVesselVDMStatus(vdmDashboardStore);
	const jobToLoadSmartAlerts =
		vdmDashboardSelectors.getJobToLoadVDMSmartAlerts(vdmDashboardStore);
	const isCNRForDateRangeLoading =
		vdmDashboardSelectors.getVDMCNRDateRangeLoad(vdmDashboardStore).isLoading;
	const isDashboardContentLoading =
		vdmDashboardSelectors.getJobToLoadVDMDashboardContent(
			vdmDashboardStore,
		).isLoading;

	return {
		vessel,
		activeTab,
		vdmVesselStatus,
		smartAlerts,
		isAlertsSidebarExpanded,
		jobToLoadVesselVDMStatus,
		jobToLoadSmartAlerts,
		cnrDataForDateRange,
		isCNRForDateRangeLoading,
		selectedVDMDashboardDate,
		selectedDateCNRId,
		dashboardContent,
		isDashboardContentLoading,
	};
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: OwnProps) {
	return {
		onTabChange(routeUrl: string) {
			dispatch(historyActions.push("#VDMDashboard", routeUrl));
		},
		vdmVesselStatusLoad(vesselId: string) {
			return dispatch(
				vdmDashboardActions.commands.vesselVDMStatusLoad(vesselId),
			);
		},
		loadVDMSmartAlerts(vesselId: string) {
			return dispatch(
				vdmDashboardActions.commands.vdmSmartAlertsLoad(vesselId),
			);
		},
		setActiveTab(tab: VDM_DASHBOARD_TABS) {
			return dispatch(vdmDashboardActions.document.vdmTabSet(tab));
		},
		toggleAlertsSidebar(isAlertsSidebarExpanded: boolean) {
			return dispatch(
				vdmDashboardActions.document.vdmDashboardAlertsToggle(
					isAlertsSidebarExpanded,
				),
			);
		},
		loadBunkerTab: (vesselId: string) => {
			return dispatch(vdmDashboardActions.commands.vdmBunkerTabLoad(vesselId));
		},
		loadConsumptionTab: (vesselId: string) => {
			return dispatch(
				vdmDashboardActions.commands.vdmConsumptionTabLoad(vesselId),
			);
		},
		loadFuelConsumptionDistanceCovered: (
			vesselId: string,
			duration: string,
		) => {
			return dispatch(
				vdmDashboardActions.commands.vdmFuelConsumptionDistanceCoveredLoad(
					vesselId,
					duration,
				),
			);
		},
		loadDraftSpeedImpactOnFuelConsumption: (vesselId: string) => {
			return dispatch(
				vdmDashboardActions.commands.vdmDraftSpeedImpactOnFuelConsumptionLoad(
					vesselId,
				),
			);
		},
		loadCNRForDateRange(params: ICNRForDateRangeParams) {
			return dispatch(vdmDashboardActions.commands.vdmCNRDateRangeLoad(params));
		},
		setSelectedVDMDashboardDate(date: string) {
			return dispatch(
				vdmDashboardActions.document.vdmDashboardSelectedDateSet(date),
			);
		},
		setCNRIdForSelectedDate(cnrId: string) {
			return dispatch(
				vdmDashboardActions.document.vmdDashboardAssociatedCNRIDForSelectedDate(
					cnrId,
				),
			);
		},
		loadVDMDashboardContent(params: IVDMDashboardLoadParams) {
			return dispatch(vdmDashboardActions.commands.vdmDashboardLoad(params));
		},
	};
}
const reduxConnector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default withRouter(reduxConnector(VDMDashboard));
